import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { fetchLeaderboard, fetchGamify } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { HERO_ART } from "@/lib/heroes";
import { Trophy, Flame, Award, BookOpen, Sparkles, Lock } from "lucide-react";

const BADGE_ICON = {
  "first-steps": Sparkles, reader: BookOpen, scholar: BookOpen,
  "japa-mala": Award, "japa-sahasra": Award, "quiz-master": Trophy,
  "streak-7": Flame, "streak-30": Flame,
};

function ProfileCard({ profile, pick }) {
  const lvl = profile.level;
  const cur = lvl.current_threshold || 0;
  const nxt = lvl.next_threshold;
  const pct = nxt ? Math.min(100, Math.round(((profile.points - cur) / (nxt - cur)) * 100)) : 100;
  const earned = new Set((profile.badges || []).map((b) => b.id));

  return (
    <div className="border border-line bg-surface p-6 md:p-8" data-testid="gamify-profile">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-label text-gold">{pick("आपकी साधना", "Your sadhana")}</p>
          <p className="mt-2 font-deva text-3xl text-ink deva-lh">{lvl.deva}</p>
          <p className="font-serif-latin text-ink-2">{lvl.en}</p>
        </div>
        <div className="text-right">
          <p className="font-serif-latin text-4xl text-saffron" data-testid="gamify-points">{profile.points}</p>
          <p className="text-xs uppercase tracking-widest text-ink-3">{pick("अंक", "points")}</p>
        </div>
      </div>

      {/* progress to next level */}
      <div className="mt-6">
        <div className="mb-1.5 flex items-center justify-between text-xs text-ink-3">
          <span>{lvl.en}</span>
          {nxt ? <span>{profile.points} / {nxt} → {lvl.next_en}</span> : <span>{pick("सर्वोच्च स्तर", "Highest level")}</span>}
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--surface-alt)]">
          <div className="h-full rounded-full bg-[var(--saffron)] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* quick stats */}
      <div className="mt-6 grid grid-cols-3 gap-3 border-t border-line pt-6 text-center">
        <div><p className="font-serif-latin text-2xl text-ink">{profile.reads}</p><p className="text-xs text-ink-3">{pick("पढ़े", "read")}</p></div>
        <div><p className="font-serif-latin text-2xl text-ink">{profile.japa_total.toLocaleString()}</p><p className="text-xs text-ink-3">{pick("जप", "japa")}</p></div>
        <div><p className="inline-flex items-center gap-1 font-serif-latin text-2xl text-saffron"><Flame className="h-4 w-4" />{profile.streak}</p><p className="text-xs text-ink-3">{pick("दिन", "streak")}</p></div>
      </div>

      {/* badges */}
      <div className="mt-8">
        <p className="mb-3 overline-label text-ink-3">{pick("उपलब्धियाँ", "Badges")} · {profile.badges.length}/{profile.all_badges.length}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {profile.all_badges.map((b) => {
            const Icon = BADGE_ICON[b.id] || Award;
            const got = earned.has(b.id);
            return (
              <div key={b.id} data-testid={`badge-${b.id}`} title={b.desc}
                className={`flex flex-col items-center gap-2 border p-4 text-center transition-colors ${got ? "border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)]" : "border-line opacity-55"}`}>
                <span className={`flex h-11 w-11 items-center justify-center rounded-full border ${got ? "border-[var(--gold)] text-gold" : "border-line text-ink-3"}`}>
                  {got ? <Icon className="h-5 w-5" /> : <Lock className="h-4 w-4" />}
                </span>
                <p className="font-deva text-sm text-ink deva-lh">{b.deva}</p>
                <p className="text-[11px] leading-tight text-ink-3">{b.en}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { pick } = useLang();
  const { user, ready } = useAuth();
  const [board, setBoard] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => { fetchLeaderboard().then(setBoard).catch(() => setBoard([])); }, []);
  useEffect(() => { if (ready && user) fetchGamify().then(setProfile).catch(() => {}); }, [ready, user]);

  return (
    <div data-testid="leaderboard-page">
      <Helmet><title>Sadhana Leaderboard · Dharmartha</title>
        <meta name="description" content="Earn points and badges for reading, japa, quizzes and daily practice. See the community sadhana leaderboard." /></Helmet>

      <PageHero
        art={HERO_ART.devotion}
        overline={pick("साधना · अंक एवं उपलब्धियाँ", "Sadhana · Points & Badges")}
        deva="साधना सूची"
        en={pick("साधना लीडरबोर्ड", "Sadhana Leaderboard")}
        tagline={pick("पढ़ने, जप, प्रश्नोत्तरी एवं नित्य दर्शन से अंक अर्जित करें, उपलब्धियाँ पाएँ और सामूहिक साधना में सहभागी बनें।", "Earn points and badges for reading, japa, quizzes and visiting daily — and grow your practice with the community.")}
      />

      <section className="mx-auto max-w-4xl px-5 py-14 md:px-8 md:py-16">
        {profile ? (
          <ProfileCard profile={profile} pick={pick} />
        ) : ready && !user ? (
          <div className="border border-line bg-[var(--surface-alt)]/40 p-6 text-center" data-testid="gamify-login-prompt">
            <p className="font-serif-latin text-lg text-ink-2">
              {pick("अपने अंक एवं उपलब्धियाँ अर्जित करने हेतु ", "To start earning points and badges, ")}
              <Link to="/register" className="text-saffron underline">{pick("निःशुल्क खाता बनाएँ", "create a free account")}</Link>.
            </p>
          </div>
        ) : null}

        {/* Leaderboard table */}
        <div className="mt-12">
          <div className="mb-5 flex items-center gap-3">
            <Trophy className="h-5 w-5 text-gold" />
            <h2 className="font-serif-latin text-2xl text-ink">{pick("सर्वाधिक अंक", "Top sadhaks")}</h2>
          </div>
          {board.length === 0 ? (
            <p className="border border-line bg-surface px-4 py-12 text-center text-ink-3">
              {pick("अभी कोई अंक नहीं — सबसे पहले आप बनें!", "No points yet — be the first to begin!")}
            </p>
          ) : (
            <div className="overflow-hidden border border-line bg-surface" data-testid="leaderboard-table">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-line bg-surface-alt/50 text-xs uppercase tracking-wider text-ink-3">
                  <tr><th className="w-14 px-4 py-3">#</th><th className="px-4 py-3">{pick("साधक", "Sadhaka")}</th><th className="hidden px-4 py-3 sm:table-cell">{pick("स्तर", "Level")}</th><th className="px-4 py-3 text-right">{pick("अंक", "Points")}</th></tr>
                </thead>
                <tbody>
                  {board.map((r, i) => (
                    <tr key={i} data-testid={`leaderboard-row-${i}`} className="border-b border-line last:border-0">
                      <td className="px-4 py-3">
                        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full font-serif-latin ${i < 3 ? "bg-[rgba(199,80,0,0.08)] text-saffron" : "text-ink-3"}`}>{i + 1}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-deva-body text-ink">{r.name}</span>
                        {r.streak > 0 && <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-saffron"><Flame className="h-3 w-3" />{r.streak}</span>}
                      </td>
                      <td className="hidden px-4 py-3 text-ink-2 sm:table-cell">{r.level.deva} · {r.level.en.split(" · ")[1] || r.level.en}</td>
                      <td className="px-4 py-3 text-right font-serif-latin text-lg text-ink">{r.points.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
